import { Component, ElementRef, HostListener, Inject, Input, OnInit, QueryList, ViewChild, ViewChildren, input } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { UserAccountId, UserContact, UserContactDTO } from "../../interfaces/user-info.interface";
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { InteractionType } from "@azure/msal-browser";
import { MatButton, MatIconButton } from "@angular/material/button";
import { getDictionaryItem } from "../../utils/dictionary.domain";
import { CEHelpersService } from "../../services/ce-helpers.service";
import {
    ContactGroups,
    ContactTitles,
    LocalizedLabel,
    MappedContactGroups,
    MappedContactMethod,
    MappedContactTitles,
    PreferredContactMethodsCode
} from "../../interfaces/ce-helpers.interface";
import { trigger, state, style, transition, animate } from "@angular/animations";
import { AbstractControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { MatFormField, MatFormFieldModule } from "@angular/material/form-field";
import { MatSelect, MatOption, MatSelectModule } from "@angular/material/select";
import { FormBuilder } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { RouterLink } from "@angular/router";
import { AccountsService } from "../../services/accounts.service";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { CdkTrapFocus } from "@angular/cdk/a11y";
import { PwaGuideComponent } from "../pwa-guide/pwa-guide.component";

@Component({
    selector: "app-user-profile",
    standalone: true,
    imports: [
        MatIcon,
        MatIconButton,
        MatButton,
        MatFormField,
        MatSelect,
        MatOption,
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        RouterLink,
        MatProgressSpinnerModule,
        CdkTrapFocus,
        PwaGuideComponent
    ],
    templateUrl: "./user-profile.component.html",
    styleUrl: "./user-profile.component.scss",
    animations: [
        trigger("fadeInOut", [state("void", style({ opacity: 0 })), transition("void <=> *", [animate(300)])]),
        trigger("slideInFromRight", [state("void", style({ transform: "translateX(100%)" })), transition("void <=> *", [animate(300)])])
    ]
})
export class UserProfileComponent implements OnInit {
    @ViewChild("formGroup") formGroup!: ElementRef;

    @Input() user: UserContact | null = null;

    userDataForm: FormGroup;
    contactPersons: UserContact[] | null = null;
    currentUserValue: UserContact | null = null;
    contactMethods: MappedContactMethod[] = [];
    contactTitles: MappedContactTitles[] = [];
    contactGroups: MappedContactGroups[] = [];

    isOpen: boolean = false;
    isSubmitting: boolean = false;
    submitError: boolean = false;
    submitSuccess: boolean = false;

    userDataFormTestId: string = "user-data-form-test-id";
    logoutButtonId: string = "logout-button-test-id";
    submitButton: string = "submit-button-test-id";
    userProfileButtonId: string = "user-profile-button-test-id";
    userProfileMenuId: string = "user-profile-menu-test-id";
    contactPersonsList: string = "contact-persons-list-test-id";

    constructor(
        @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
        private authService: MsalService,
        private formBuilder: FormBuilder,
        private helpers: CEHelpersService,
        private accountService: AccountsService
    ) {
        const contactGroupArray = this.user?.contactgroups.split(",").map(Number);

        this.userDataForm = this.formBuilder.group({
            title: [this.user?.title],
            contactgroups: [contactGroupArray],
            mobilephone: [this.user?.mobilephone],
            landlinePhone: [this.user?.landlinePhone],
            svk_smsnotificationnumber: [this.user?.svk_smsnotificationnumber],
            preferredContactMethod: [this.user?.preferredContactMethod]
        });
    }

    @ViewChildren("contactPersonButton") contactPersonButtons: QueryList<ElementRef> | undefined;

    @HostListener("keydown", ["$event"])
    handleKeyboardEvent(event: KeyboardEvent) {
        const buttons = this.contactPersonButtons?.toArray();
        const currentIndex = buttons?.findIndex((button) => button.nativeElement === document.activeElement);

        if (event.key === "ArrowDown" && buttons) {
            event.preventDefault();
            const nextIndex = (currentIndex !== undefined ? currentIndex + 1 : 0) % buttons.length;
            buttons[nextIndex].nativeElement.focus();
        }
        if (event.key === "ArrowUp" && buttons) {
            event.preventDefault();
            if (currentIndex !== undefined) {
                const prevIndex = (currentIndex - 1 + buttons.length) % buttons.length;
                buttons[prevIndex].nativeElement.focus();
            }
        }
    }

    @HostListener("document:keydown", ["$event"])
    handleTabKeyPress(event: KeyboardEvent): void {
        if (event.key === "Escape") {
            this.closeUserProfile();
        }
    }

    ngOnInit() {
        this.helpers.getPreferredContactMethods().subscribe((methods: PreferredContactMethodsCode) => {
            if (methods) {
                const mappedMethods = this.helpers.mapContactMethods(methods);
                if (!mappedMethods) return undefined;
                this.contactMethods = mappedMethods;
            }
        });

        this.helpers.getContactTitles().subscribe((titles: ContactTitles) => {
            if (!titles) return;
            const mappedTitles = this.helpers.mapContactTitles(titles);
            if (!mappedTitles) return;
            this.contactTitles = mappedTitles;
        });

        this.helpers.getContactGroups().subscribe((groups: ContactGroups) => {
            if (!groups) return;
            const mappedGroups = this.helpers.mapContactGroups(groups);
            if (!mappedGroups) return;
            this.contactGroups = mappedGroups;
        });

        if (this.user) {
            this.currentUserValue = this.user;
            this.prefillForm();
        }
    }

    get isMobile(): boolean {
        return window.innerWidth < 787;
    }

    public areAllFieldsDisabled(): boolean {
        const formControls = this.userDataForm.controls;
        return Object.keys(formControls).every((key) => formControls[key].disabled);
    }

    public toggleUserProfile() {
        this.isOpen = !this.isOpen;

        if (this.isOpen && this.contactPersons === null) {
            const accountId = localStorage.getItem("accountid") ?? this.user?.currentAccount.accountId ?? null;
            if (accountId) {
                this.accountService.getConnectedContactPersons(accountId).subscribe((persons) => {
                    if (!persons) return;
                    this.contactPersons = persons;
                });
            } else console.log("No account id found");
        }

        if (this.isOpen && this.currentUserValue !== this.user) {
            this.setContactPerson(this.user as UserContact, 0);
        }
    }

    public closeUserProfile() {
        this.isOpen = false;
    }

    public getLocalizedLabel(labels: LocalizedLabel[], languageCode?: number): LocalizedLabel | undefined {
        if (!languageCode) {
            languageCode = 1030;
        }

        let label = labels.find((label) => label.LanguageCode === languageCode);

        if (!label) {
            label = labels[0];
        }

        return label;
    }

    public enableFieldState(field: string, isEditable: boolean): void {
        if (isEditable) {
            const fieldId = document.getElementById("form-field-" + field);
            this.userDataForm.get(field)?.enable();
            if (field === "mobilephone" || field === "landlinePhone" || field === "svk_smsnotificationnumber") {
                this.userDataForm.get(field)?.setValidators([Validators.pattern("^[0-9]*$"), this.mobileFieldValidator]);
            }
            if (fieldId) {
                fieldId?.focus();
            }
        }
    }

    public disabledFieldState(field: keyof UserContact, isEditable: boolean): void {
        if (isEditable) {
            // resets the field to the original value if the user has not saved the changes
            if (field === "contactgroups") {
                this.userDataForm?.patchValue({ [field]: this.currentUserValue?.[field]?.split(",").map(Number) });
            } else {
                this.userDataForm?.patchValue({ [field]: this.currentUserValue?.[field] });
            }

            this.userDataForm?.get(field)?.disable();
        }
    }

    mobileFieldValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value === null || value.length === 0 || value.length === 8) {
            return null;
        }
        return { lengthError: "Field must be either 0 or 8 characters long" };
    }

    public setContactPerson(contactPerson: UserContact, timeout: number = 1000): void {
        this.isSubmitting = true;
        this.currentUserValue = contactPerson;
        this.prefillForm(contactPerson);

        setTimeout(() => {
            // doing this to indicate to the user, that the form is changing
            this.isSubmitting = false;
        }, timeout);
    }

    prefillForm(currentUser: UserContact | null = this.user): void {
        const contactGroupArray = currentUser?.contactgroups?.split(",").map(Number);
        this.userDataForm = this.formBuilder.group({
            mobilephone: [{ value: currentUser?.mobilephone, disabled: true }],
            landlinePhone: [{ value: currentUser?.landlinePhone, disabled: true }],
            svk_smsnotificationnumber: [{ value: currentUser?.svk_smsnotificationnumber, disabled: true }],
            preferredContactMethod: [{ value: currentUser?.preferredContactMethod, disabled: true }],
            contactgroups: [{ value: contactGroupArray, disabled: true }],
            title: [{ value: currentUser?.title, disabled: true }]
        });
    }

    public onSubmit(contactId: string | undefined) {
        this.isSubmitting = true;
        const formValues = this.userDataForm?.value;
        const body: Partial<UserContactDTO> = {};

        // Form mapping
        for (const key in formValues) {
            if (formValues.hasOwnProperty(key) && formValues[key] !== this.user?.[key as keyof UserContact]) {
                switch (key) {
                    case "mobilephone":
                        body.mobilephone = formValues[key];
                        break;
                    case "landlinePhone":
                        body.telephone3 = formValues[key];
                        break;
                    case "svk_smsnotificationnumber":
                        body.svk_smsnotificationnumber = formValues[key];
                        break;
                    case "preferredContactMethod":
                        body.preferredcontactmethodcode = formValues[key];
                        break;
                    case "contactgroups":
                        body.svk_contactgroup = formValues[key].join(",");
                        break;
                    case "title":
                        body.svk_title = formValues[key];
                        break;
                    default:
                        break;
                }
            }
        }

        // TODO: snak med Allan om denne. Lige nu giver den fejl hvis en bruger forsøger at opdatere sine egne oplysninger
        // og man sender contactId med. Det virker hvis man opdaterer en anden bruger.
        if (this.user?.contactId && this.user?.contactId !== contactId) {
            const id = this.user.contactId;
            body["svk_LastModifiedOnlineBy@odata.bind"] = `/contacts(${id})`;
        }

        const now = new Date();
        // we convert the timezone to CET timezone, for more accurate data on in CE
        now.setHours(now.getHours() + 1);
        const isoDate = now.toISOString();

        body["svk_lastmodifiedonline"] = isoDate;

        this.accountService.updateContactPerson(contactId, body).subscribe((contactPerson) => {
            try {
                if (!contactPerson) {
                    this.isSubmitting = false;
                    this.submitError = true;
                    return;
                }

                // returns all fields to disabled state
                this.userDataForm?.get("mobilephone")?.disable();
                this.userDataForm?.get("landlinePhone")?.disable();
                this.userDataForm?.get("preferredContactMethod")?.disable();
                this.userDataForm?.get("svk_smsnotificationnumber")?.disable();
                this.userDataForm?.get("contactgroups")?.disable();
                this.userDataForm?.get("title")?.disable();

                // resets the form to the original values
                this.submitError = false;
                this.submitSuccess = true;
                this.isSubmitting = false;
                setTimeout(() => {
                    this.submitSuccess = false;
                }, 5000);
                return contactPerson;
            } catch {
                this.isSubmitting = false;
                this.submitError = true;
                return;
            }
        });
    }

    public logout() {
        if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
            this.authService.logoutPopup({
                mainWindowRedirectUri: "/"
            });
            localStorage.removeItem("accountid");
        } else {
            this.authService.logoutRedirect();
            localStorage.removeItem("accountid");
        }
    }

    public getDictionaryItem = getDictionaryItem;
}
